import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';

import { VariableSummary } from '@mtna/pojo-consumer-ui';

import { RdsResizeableTableComponent } from '../core/components/resizeable-table.component';
import {
  RDS_DATA_DICTIONARY_ALL_COLUMNS,
  RDS_DATA_DICTIONARY_COLUMNS,
  RdsDataDictionaryTableColumn,
} from './data-dictionary-table-column.model';

export const RDS_DICTIONARY_TABLE_MIN_COL_WIDTH = 50;
export const RDS_DICTIONARY_DEFAULT_COLUMNS = RDS_DATA_DICTIONARY_ALL_COLUMNS.slice(0, 8);

export class RdsDataDictionaryToggleEvent {
  constructor(public selected: boolean, public variable: VariableSummary) {}
}

/**
 * Custom mat-table used to display variables in a data-dictionary view.
 * Ability to select variables(rows)
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-data-dictionary-table',
  templateUrl: './data-dictionary-table.component.html',
  styleUrls: ['./data-dictionary-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsDataDictionaryTableComponent extends RdsResizeableTableComponent implements OnInit {
  /**
   * All the column options to choose from. Defaults to:
   * `RDS_DATA_DICTIONARY_ALL_COLUMNS.slice()`
   */
  @Input() allColumnOptions: Array<RdsDataDictionaryTableColumn> = RDS_DATA_DICTIONARY_ALL_COLUMNS.slice();
  /**
   * The default, visible table columns to render. Defaults to:
   * `RDS_DATA_DICTIONARY_ALL_COLUMNS.slice(0, 5)`
   */
  @Input() get columns(): Array<RdsDataDictionaryTableColumn> {
    return !!this._columns ? this._columns : RDS_DICTIONARY_DEFAULT_COLUMNS;
  }
  set columns(cols: Array<RdsDataDictionaryTableColumn>) {
    this._columns = cols;
    // determine and set displayable columns for the table
    // sort by index, and use the property names as column IDs
    this.displayedColumns = !this.columns
      ? []
      : this.columns
          .slice()
          .sort((a, b) => a.index - b.index)
          .map((c) => c.property);
    if (this.enableColumnResize) {
      this.initializeColumnWidths();
    }
  }
  private _columns: Array<RdsDataDictionaryTableColumn>;

  /** Whether the variables are loading, ghost elements will render in their place */
  @Input() loading: boolean;
  /**
   * Whether the toggled variables represent the selected, or unselected.
   * For instance, if the user selects 100+ variables, and unselects 5,
   * the 'toggled' variables will be the 5 unselected & 'toggledAreSelected' will be false;
   * i.e.
   * user selects 95 out of 100 => toggledAreSelected: false, toggled.length: 5;;
   * user selects 5 out of 100 => toggledAreSelected: true, toggled.length: 5;
   */
  @Input()
  get toggledAreSelected() {
    return this._toggledAreSelected;
  }
  set toggledAreSelected(selected: any) {
    selected = coerceBooleanProperty(selected);
    this._toggledAreSelected = selected;
  }
  private _toggledAreSelected = true;
  /** The set of (un)selected variables toggled by the user */
  @Input()
  set toggledUris(variableUris: string[]) {
    this._toggledUris = {};
    variableUris.forEach((uri) => (this._toggledUris[uri] = true));
  }
  /** uri map of variables currently toggled */
  _toggledUris: { [uri: string]: boolean } = {};

  /** Variables to display in the table */
  @Input()
  get variables(): Array<VariableSummary> {
    return this._variables;
  }
  set variables(variables: Array<VariableSummary>) {
    this._variables = variables;
  }
  private _variables: Array<VariableSummary> = [];

  /** Event emitted when the user toggles an individual variable selection */
  @Output() toggle = new EventEmitter<RdsDataDictionaryToggleEvent>();
  /** Event emitted when the user selects all visible variables */
  @Output() selectAll = new EventEmitter<VariableSummary[]>();
  /** Event emitted when the user deselects all visible variables */
  @Output() selectNone = new EventEmitter<VariableSummary[]>();
  /** Emitted to show additional variable details */
  @Output() viewDetails = new EventEmitter<VariableSummary>();

  get areColumnsSet(): boolean {
    return !!this.columns;
  }

  /** Whether the table has at least 1 toggled variable */
  get hasChecked(): boolean {
    return !!this.variables && this.variables.some((v) => !!this._toggledUris[v.uri]);
  }

  get minColWidth(): number {
    return RDS_DICTIONARY_TABLE_MIN_COL_WIDTH;
  }

  constructor(cdr: ChangeDetectorRef) {
    super(cdr);
  }

  handleSelectAll() {
    this.selectAll.emit(this.variables);
  }

  /** Whether all the visible variables are selected */
  isAllSelected(): boolean {
    return (
      !!this.variables && this.variables.every((v) => (this._toggledAreSelected ? this._toggledUris[v.uri] : !this._toggledUris[v.uri]))
    );
  }

  ngOnInit() {
    // If no columns were set, initialize with the default
    if (!this._columns) {
      // determine and set displayable columns for the table
      // sort by index, and use the property names as column IDs
      this.displayedColumns = !this.columns
        ? []
        : this.columns
            .slice()
            .sort((a, b) => a.index - b.index)
            .map((c) => c.property);
      if (this.enableColumnResize) {
        this.initializeColumnWidths();
      }
    }
  }

  masterToggle(event: MatCheckboxChange) {
    event.checked ? this.handleSelectAll() : this.selectNone.emit(this.variables);
  }

  protected getColumnWidth(id: RDS_DATA_DICTIONARY_COLUMNS): number {
    switch (id) {
      case 'isMeasure':
      case 'isRequired':
      case 'isWeight':
      case 'decimals':
        return 90;
      case 'startPosition':
      case 'endPosition':
      case 'dataType':
        return 100;
      case 'hasClassification':
        return 124;
      case 'label':
        return 200;
      case 'questionText':
        return 400;
      default:
        return super.getColumnWidth(id);
    }
  }
}
